import { useCallback, useEffect, useState } from "react";
import { Bot, Clock, Check, X, Loader2, Ban } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/gp";
import { Button } from "@/components/ui/button";

const STATUS_LABEL = {
  queued: { label: "Queued", icon: Clock, className: "text-amber-300" },
  sent: { label: "Sent", icon: Check, className: "text-gp-green" },
  reverted: { label: "Cancelled", icon: Ban, className: "text-white/40" },
  failed: { label: "Failed to send", icon: X, className: "text-gp-red" },
};

function timeLeft(sendAt) {
  const ms = new Date(sendAt).getTime() - Date.now();
  if (ms <= 0) return "sending now";
  const mins = Math.ceil(ms / 60000);
  return `sends in ~${mins} min`;
}

export default function AutoRepliesAdmin() {
  const [replies, setReplies] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    apiFetch("/admin/yorick/replies")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setReplies(d.replies || []))
      .catch(() => toast.error("Could not load Yorick's auto-replies."));
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 20000);
    return () => clearInterval(interval);
  }, [load]);

  const revert = async (id) => {
    setBusyId(id);
    try {
      const res = await apiFetch(`/admin/yorick/replies/${id}/revert`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not cancel this reply.");
      toast.success("Reply cancelled — it will not be sent.");
      load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusyId(null);
    }
  };

  const queued = (replies || []).filter((r) => r.status === "queued");
  const history = (replies || []).filter((r) => r.status !== "queued");

  return (
    <section className="glass rounded-3xl p-7 mb-8 border border-white/10" data-testid="admin-autoreplies">
      <div className="flex items-center gap-2 text-gp-blue">
        <Bot size={20} /><span className="font-unbounded font-bold uppercase">Yorick's Auto-Replies</span>
      </div>
      <p className="mt-2 max-w-2xl text-sm text-white/55">
        Yorick drafts and sends a first-reply email to every support message automatically. Each one
        waits a few minutes before sending — cancel it here if it's wrong before it goes out. Once sent,
        it can't be recalled.
      </p>

      {!replies ? (
        <div className="mt-5 flex items-center gap-2 text-sm text-white/55"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : (
        <>
          {queued.length > 0 && (
            <div className="mt-5 space-y-3">
              <h3 className="text-xs uppercase tracking-widest text-amber-300">Pending — cancel window open</h3>
              {queued.map((r) => (
                <div key={r.id} className="rounded-xl border border-amber-400/20 bg-amber-400/5 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{r.subject} → {r.to_name} &lt;{r.to_email}&gt;</p>
                      <p className="text-xs text-amber-300 mt-1">{timeLeft(r.send_at)}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => revert(r.id)} disabled={busyId === r.id} className="rounded-full border-white/15 bg-white/5 shrink-0">
                      {busyId === r.id ? <Loader2 className="animate-spin mr-1.5" size={13} /> : <Ban size={13} className="mr-1.5" />} Cancel
                    </Button>
                  </div>
                  <p className="text-sm text-white/70 mt-2 whitespace-pre-wrap">{r.body}</p>
                </div>
              ))}
            </div>
          )}

          {history.length > 0 && (
            <div className="mt-6 space-y-2">
              <h3 className="text-xs uppercase tracking-widest text-white/40">History</h3>
              {history.slice(0, 20).map((r) => {
                const status = STATUS_LABEL[r.status] || STATUS_LABEL.sent;
                const Icon = status.icon;
                return (
                  <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/20 px-4 py-2.5 text-sm">
                    <span className="truncate text-white/70">{r.subject} → {r.to_email}</span>
                    <span className={`flex items-center gap-1.5 shrink-0 ${status.className}`}><Icon size={13} /> {status.label}</span>
                  </div>
                );
              })}
            </div>
          )}

          {replies.length === 0 && <p className="mt-5 text-sm text-white/40">No support messages yet.</p>}
        </>
      )}
    </section>
  );
}
